import React, { useState, useEffect } from 'react';
import {
  Users,
  Target,
  Eye,
  Heart,
  BookOpen,
  Globe,
  TrendingUp,
  Award,
  Shield,
  CheckCircle,
  Mail,
  User,
  Building2,
  FileText,
  Send,
  Lightbulb,
  Network,
  GraduationCap,
  Stethoscope,
  Cpu,
  Sprout,
  Loader2
} from 'lucide-react';
import { useFormSubmit } from '../hooks/useFormSubmit';

const RedRiel = () => {
  const [redData, setRedData] = useState(null);
  const [datosBancarios, setDatosBancarios] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [formData, setFormData] = useState({
    tipo: 'docente',
    nombre: '',
    email: '',
    linea: '',
    institucion: '',
    mensaje: '',
    renovacionMembresia: false,
    numeroMembresia: '',
    acepto: false
  });
  const { loading: isSubmitting, success: isSuccess, error: submitError, submitForm, resetForm: resetFormApi } = useFormSubmit('redriel');

  useEffect(() => {
    const cargarDatos = async () => {
      try {
        const v = Date.now();
        const [redResponse, generalesResponse] = await Promise.all([
          fetch(`/data/redriel.json?v=${v}`, { cache: 'no-store' }),
          fetch(`/data/generales.json?v=${v}`, { cache: 'no-store' })
        ]);

        const redData = await redResponse.json();
        const generalesData = await generalesResponse.json();

        setRedData(redData);
        setDatosBancarios(generalesData.datosBancarios);
      } catch (error) {
        console.error('Error al cargar los datos:', error);
      } finally {
        setIsLoading(false);
      }
    };

    cargarDatos();
  }, []);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    await submitForm(formData);
  };

  const resetForm = () => {
    setFormData({
      tipo: 'docente',
      nombre: '',
      email: '',
      linea: '',
      institucion: '',
      mensaje: '',
      renovacionMembresia: false,
      numeroMembresia: '',
      acepto: false
    });
    resetFormApi();
  };

  // Auto reset form on success
  useEffect(() => {
    if (isSuccess) {
      resetForm();
    }
  }, [isSuccess]);

  const getIconComponent = (iconName) => {
    const icons = {
      Lightbulb,
      Building2,
      GraduationCap,
      Stethoscope,
      Cpu,
      Sprout
    };
    return icons[iconName] || Lightbulb;
  };

  // Estado para grupos de trabajo expandidos (accordion)
  const [expandedGroups, setExpandedGroups] = useState({});

  const toggleGroup = (groupKey) => {
    setExpandedGroups(prev => {
      const isCurrentlyOpen = prev[groupKey];
      // Si está abierto, cerrarlo. Si está cerrado, abrirlo y cerrar todos los demás
      return isCurrentlyOpen ? {} : { [groupKey]: true };
    });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-[#0b742f] animate-spin mx-auto mb-4" />
          <p className="text-gray-600 text-lg">Cargando información de RedRiel...</p>
        </div>
      </div>
    );
  }

  if (!redData) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600 text-lg">Error al cargar los datos de RedRiel</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Hero Section */}
      <div className="relative bg-gradient-to-r from-[#0b742f] to-[#085a24] text-white py-20 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAxMCAwIEwgMCAwIDAgMTAiIGZpbGw9Im5vbmUiIHN0cm9rZT0id2hpdGUiIHN0cm9rZS13aWR0aD0iMSIvPjwvcGF0dGVybj48L2RlZnM+PHJlY3Qgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgZmlsbD0idXJsKCNncmlkKSIvPjwvc3ZnPg==')] opacity-20"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-6">
          <div className="flex items-start gap-4 mb-6">
            <div className="bg-white/10 backdrop-blur-sm p-4 rounded-xl">
              <Network className="w-12 h-12" />
            </div>
            <div>
              <h1 className="text-4xl md:text-5xl font-bold mb-4">
                {redData.hero.titulo}
              </h1>
              <p className="text-xl md:text-2xl text-white/90 max-w-3xl">
                {redData.hero.subtitulo}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-12">

        {/* Antecedentes */}
        <section className="mb-12">
          <div className="bg-white rounded-lg shadow-xl overflow-hidden border-t-4 border-[#0b742f]">
            <div className="p-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                <BookOpen className="w-8 h-8 text-[#0b742f]" />
                Antecedentes
              </h2>
              <p className="text-lg text-gray-700 leading-relaxed">
                {redData.antecedentes.texto}
              </p>
            </div>
          </div>
        </section>

        {/* Objetivo, Misión, Visión */}
        <section className="mb-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Objetivo */}
            <div className="bg-white rounded-lg shadow-xl overflow-hidden border-t-4 border-blue-600">
              <div className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="bg-blue-100 p-3 rounded-full">
                    <Target className="w-8 h-8 text-blue-600" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900">Objetivo</h3>
                </div>
                <p className="text-gray-700 leading-relaxed">
                  {redData.objetivo.texto}
                </p>
              </div>
            </div>

            {/* Misión */}
            <div className="bg-white rounded-lg shadow-xl overflow-hidden border-t-4 border-[#0b742f]">
              <div className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="bg-green-100 p-3 rounded-full">
                    <Award className="w-8 h-8 text-[#0b742f]" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900">Misión</h3>
                </div>
                <p className="text-gray-700 leading-relaxed">
                  {redData.mision.texto}
                </p>
              </div>
            </div>

            {/* Visión */}
            <div className="bg-white rounded-lg shadow-xl overflow-hidden border-t-4 border-purple-600">
              <div className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="bg-purple-100 p-3 rounded-full">
                    <Eye className="w-8 h-8 text-purple-600" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900">Visión</h3>
                </div>
                <p className="text-gray-700 leading-relaxed">
                  {redData.vision.texto}
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Valores */}
        <section className="mb-12">
          <div className="bg-white rounded-lg shadow-xl overflow-hidden border-t-4 border-[#e87729]">
            <div className="p-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                <Heart className="w-8 h-8 text-[#e87729]" />
                Valores
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {redData.valores.map((valor, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-3 p-4 bg-gradient-to-br from-orange-50 to-amber-50 rounded-lg border-l-4 border-[#e87729] hover:shadow-lg transition-shadow duration-200"
                  >
                    <CheckCircle className="w-5 h-5 text-[#e87729] flex-shrink-0" />
                    <span className="text-gray-800 font-medium">{valor}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Comité Directivo */}
        <section className="mb-12">
          <div className="bg-white rounded-lg shadow-xl overflow-hidden border-t-4 border-[#0b742f]">
            <div className="p-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                <Shield className="w-8 h-8 text-[#0b742f]" />
                Nuestro Equipo
              </h2>

              {/* Comité Directivo */}
              <div className="mb-8">
                <h3 className="text-2xl font-bold text-[#0b742f] mb-4">Comité Directivo</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {redData.equipo.comiteDirectivo.map((miembro, index) => (
                    <div
                      key={index}
                      className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg p-5 border-2 border-green-200 hover:border-[#0b742f] hover:shadow-lg transition-all duration-200"
                    >
                      <div className="flex items-start gap-3">
                        <div className="bg-white p-2 rounded-full shadow-sm">
                          <User className="w-5 h-5 text-[#0b742f]" />
                        </div>
                        <div className="flex-1">
                          <h4 className="font-bold text-gray-900 mb-1">{miembro.nombre}</h4>
                          <p className="text-sm text-gray-600 mb-1">{miembro.pais}</p>
                          <p className="text-sm font-semibold text-[#0b742f]">{miembro.cargo}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Comité Consultivo */}
              <div>
                <h3 className="text-2xl font-bold text-blue-600 mb-4">Comité Consultivo</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                  {redData.equipo.comiteConsultivo.map((miembro, index) => (
                    <div
                      key={index}
                      className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-lg p-4 border border-blue-200 hover:border-blue-400 hover:shadow-md transition-all duration-200"
                    >
                      <div className="flex items-center gap-2">
                        <Globe className="w-4 h-4 text-blue-600 flex-shrink-0" />
                        <div>
                          <p className="font-semibold text-gray-900 text-sm">{miembro.nombre}</p>
                          <p className="text-xs text-gray-600">{miembro.pais}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Colaboración, Difusión, Impacto */}
        <section className="mb-12">
          <div className="bg-white rounded-lg shadow-xl overflow-hidden border-t-4 border-purple-600">
            <div className="p-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-8 flex items-center gap-3">
                <TrendingUp className="w-8 h-8 text-purple-600" />
                Nuestras Líneas de Acción
              </h2>

              <div className="space-y-8">
                {/* Colaboración */}
                <div className="border-l-4 border-[#0b742f] pl-6">
                  <h3 className="text-2xl font-bold text-[#0b742f] mb-4 flex items-center gap-2">
                    <Users className="w-6 h-6" />
                    Colaboración
                  </h3>
                  <ul className="space-y-3">
                    {redData.lineasAccion.colaboracion.map((item, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-[#0b742f] flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Difusión */}
                <div className="border-l-4 border-blue-600 pl-6">
                  <h3 className="text-2xl font-bold text-blue-600 mb-4 flex items-center gap-2">
                    <Globe className="w-6 h-6" />
                    Difusión
                  </h3>
                  <ul className="space-y-3">
                    {redData.lineasAccion.difusion.map((item, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Impacto */}
                <div className="border-l-4 border-[#e87729] pl-6">
                  <h3 className="text-2xl font-bold text-[#e87729] mb-4 flex items-center gap-2">
                    <Award className="w-6 h-6" />
                    Impacto
                  </h3>
                  <ul className="space-y-3">
                    {redData.lineasAccion.impacto.map((item, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-[#e87729] flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Áreas Estratégicas */}
        <section className="mb-12">
          <div className="bg-white rounded-lg shadow-xl overflow-hidden border-t-4 border-[#0b742f]">
            <div className="p-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-4 flex items-center gap-3">
                <Lightbulb className="w-8 h-8 text-[#0b742f]" />
                Áreas Estratégicas
              </h2>
              <div 
                className="text-gray-700 mb-6"
                dangerouslySetInnerHTML={{ __html: redData.areasEstrategicas.descripcion }}
              />

              <h3 className="text-xl font-bold text-gray-900 mb-4">Grupos de trabajo</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {redData.areasEstrategicas.grupos.map((area, index) => {
                  const IconComponent = getIconComponent(area.icon);
                  const colorClasses = {
                    blue: 'from-blue-50 to-cyan-50 border-blue-500 text-blue-600',
                    purple: 'from-purple-50 to-violet-50 border-purple-500 text-purple-600',
                    green: 'from-green-50 to-emerald-50 border-[#0b742f] text-[#0b742f]',
                    red: 'from-red-50 to-rose-50 border-red-500 text-red-600',
                    orange: 'from-orange-50 to-amber-50 border-[#e87729] text-[#e87729]',
                    emerald: 'from-emerald-50 to-teal-50 border-emerald-500 text-emerald-600'
                  };

                  return (
                    <div
                      key={index}
                      className={`bg-gradient-to-br ${colorClasses[area.color]} rounded-lg p-5 border-l-4 hover:shadow-lg transition-all duration-200`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="bg-white p-2 rounded-full shadow-sm">
                          <IconComponent className={`w-6 h-6 ${colorClasses[area.color].split(' ')[2]}`} />
                        </div>
                        <h4 className="font-bold text-gray-900">{area.nombre}</h4>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </section>

        {/* Grupos de Trabajo Detallados */}
        <section className="mb-12">
          <div className="bg-white rounded-lg shadow-xl overflow-hidden border-t-4 border-[#0b742f]">
            <div className="p-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                <Users className="w-8 h-8 text-[#0b742f]" />
                Grupos de Trabajo - Cuerpos Académicos
              </h2>

              {/* Mapeo dinámico de grupos */}
              {Object.entries(redData.cuerposAcademicos || {}).map(([grupoKey, cuerpos], grupoIndex) => {
                const grupoConfig = {
                  educacionHumanidadesArtes: {
                    nombre: 'Educación, Humanidades y Artes',
                    icon: GraduationCap,
                    color: 'text-[#0b742f]',
                    bgColor: 'bg-gradient-to-br from-green-50 to-emerald-50',
                    borderColor: 'border-[#0b742f]'
                  },
                  naturalesExactas: {
                    nombre: 'Naturales y Exactas',
                    icon: Lightbulb,
                    color: 'text-blue-600',
                    bgColor: 'bg-gradient-to-br from-blue-50 to-cyan-50',
                    borderColor: 'border-blue-600'
                  },
                  ingenieriaTecnologia: {
                    nombre: 'Ingeniería y Tecnología',
                    icon: Cpu,
                    color: 'text-orange-600',
                    bgColor: 'bg-gradient-to-br from-orange-50 to-amber-50',
                    borderColor: 'border-orange-600'
                  },
                  salud: {
                    nombre: 'Salud',
                    icon: Stethoscope,
                    color: 'text-red-600',
                    bgColor: 'bg-gradient-to-br from-red-50 to-rose-50',
                    borderColor: 'border-red-600'
                  },
                  socialesAdministrativas: {
                    nombre: 'Sociales y Administrativas',
                    icon: Building2,
                    color: 'text-purple-600',
                    bgColor: 'bg-gradient-to-br from-purple-50 to-violet-50',
                    borderColor: 'border-purple-600'
                  },
                  agropecuarias: {
                    nombre: 'Agropecuarias',
                    icon: Sprout,
                    color: 'text-emerald-600',
                    bgColor: 'bg-gradient-to-br from-emerald-50 to-green-50',
                    borderColor: 'border-emerald-600'
                  }
                };

                const config = grupoConfig[grupoKey];
                if (!config) return null;

                const IconComponent = config.icon;

                const isGroupOpen = !!expandedGroups[grupoKey];

                return (
                  <div key={grupoKey} className="mb-10">
                    <button
                      type="button"
                      onClick={() => toggleGroup(grupoKey)}
                      aria-expanded={isGroupOpen}
                      className={`w-full text-left flex items-center gap-3 mb-6 pb-3 border-b-2 ${config.borderColor} hover:opacity-75 transition-opacity`}
                    >
                      <IconComponent className={`w-8 h-8 ${config.color}`} />
                      <h3 className={`text-2xl font-bold ${config.color} flex-1`}>{config.nombre}</h3>
                      <div className={`text-gray-700 text-xl ${config.color}`}>{isGroupOpen ? '▲' : '▼'}</div>
                    </button>

                    <div className={`space-y-6 transition-all duration-300 overflow-hidden ${isGroupOpen ? 'max-h-none opacity-100' : 'max-h-0 opacity-0'}`}>
                      {cuerpos.map((cuerpo, index) => (
                        <div key={index} className={`${config.bgColor} rounded-lg p-6 border-l-4 ${config.borderColor}`}>
                          <h4 className="text-xl font-bold text-gray-900 mb-3">{cuerpo.nombre}</h4>

                          {cuerpo.lgac && cuerpo.lgac.length > 0 && (
                            <div className="mb-3">
                              <p className="text-sm font-semibold text-gray-700 mb-2">LGAC:</p>
                              <ul className="list-disc list-inside space-y-1 text-gray-600">
                                {cuerpo.lgac.map((lgac, lgacIndex) => (
                                  <li key={lgacIndex}>{lgac}</li>
                                ))}
                              </ul>
                            </div>
                          )}

                          {cuerpo.miembros && cuerpo.miembros.length > 0 && (
                            <div>
                              <p className="text-sm font-semibold text-gray-700 mb-2">Miembros:</p>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                {cuerpo.miembros.map((miembro, miembroIndex) => {
                                  // Determinar color del badge según el grupo
                                  const badgeColorClasses = {
                                    'text-[#0b742f]': 'bg-[#0b742f]',
                                    'text-blue-600': 'bg-blue-600',
                                    'text-orange-600': 'bg-orange-600',
                                    'text-red-600': 'bg-red-600',
                                    'text-purple-600': 'bg-purple-600',
                                    'text-emerald-600': 'bg-emerald-600'
                                  };
                                  const badgeColor = badgeColorClasses[config.color] || 'bg-gray-600';

                                  return (
                                    <p key={miembroIndex} className="text-gray-800">
                                      <span className={`font-semibold ${config.color}`}>•</span> {miembro.nombre}
                                      {miembro.esLider && (
                                        <span className={`text-xs ${badgeColor} text-white px-2 py-0.5 rounded ml-2`}>
                                          Líder
                                        </span>
                                      )}
                                    </p>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Instituciones Afiliadas */}
        <section className="mb-12">
          <div className="bg-white rounded-lg shadow-xl overflow-hidden border-t-4 border-[#0b742f]">
            <div className="p-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-8 flex items-center gap-3">
                <Building2 className="w-8 h-8 text-[#0b742f]" />
                Instituciones afiliadas
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
                {(redData.institucionesAfiliadas || []).map((inst, idx) => (
                  <div key={idx} className="flex flex-col items-center gap-4 bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-6 border border-green-200 hover:shadow-lg transition-shadow">
                    <img src={inst.logo} alt={inst.institucion} className="h-20 w-auto object-contain" />
                    <p className="font-semibold text-gray-900 text-center text-sm">{inst.institucion}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Redes de colaboración incorporadas al CENID */}
        <section className="mb-12">
          <div className="bg-white rounded-lg shadow-xl overflow-hidden border-t-4 border-blue-600">
            <div className="p-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-8 flex items-center gap-3">
                <Globe className="w-8 h-8 text-blue-600" />
                Redes de colaboración incorporadas al CENID
              </h2>
              <div className="space-y-12">
                {(redData.redesColaboracion || []).map((red, ridx) => (
                  <div key={ridx} className="border border-gray-200 rounded-xl overflow-hidden">
                    {/* Cabecera de la red */}
                    <div className="bg-gradient-to-r from-blue-50 to-cyan-50 border-b border-gray-200 p-6 flex flex-col sm:flex-row items-center gap-6">
                      <img src={red.logo} alt={red.nombre} className="h-20 w-auto object-contain flex-shrink-0" />
                      <div>
                        <h3 className="text-xl font-bold text-gray-900">{red.nombre}</h3>
                        {red.subtitulo && (
                          <p className="text-blue-600 font-semibold mt-1">{red.subtitulo}</p>
                        )}
                      </div>
                    </div>
                    {/* Lista de participantes */}
                    <div className="p-6">
                      <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-4">
                        Participantes ({red.participantes.length})
                      </h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                        {red.participantes.map((p, pidx) => (
                          <div
                            key={pidx}
                            className={`flex items-start gap-2 rounded-lg px-3 py-2 ${
                              p.esLider
                                ? 'bg-gradient-to-r from-[#0b742f] to-[#085a24] text-white'
                                : 'bg-gray-50 border border-gray-200'
                            }`}
                          >
                            <User className={`w-4 h-4 flex-shrink-0 mt-0.5 ${p.esLider ? 'text-green-200' : 'text-gray-400'}`} />
                            <div>
                              <p className={`text-sm font-medium leading-tight ${p.esLider ? 'text-white' : 'text-gray-800'}`}>
                                {p.nombre}
                              </p>
                              {p.rol && (
                                <p className="text-xs text-green-200 mt-0.5">{p.rol}</p>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Membresías */}
        <section className="mb-12">
          <div className="bg-white rounded-lg shadow-xl overflow-hidden border-t-4 border-[#e87729]">
            <div className="p-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                <Award className="w-8 h-8 text-[#e87729]" />
                Membresías
              </h2>

              {/* ¿Quiénes pueden ser parte? */}
              <div className="bg-gradient-to-r from-orange-50 to-amber-50 rounded-lg p-6 mb-8 border-l-4 border-[#e87729]">
                <h3 className="text-2xl font-bold text-gray-900 mb-3">
                  ¿Quiénes pueden ser parte de nuestra red?
                </h3>
                <p className="text-gray-700 text-lg">
                  {redData.membresias.quienesPueden}
                </p>
              </div>

              {/* Beneficios */}
              <h3 className="text-2xl font-bold text-[#0b742f] mb-4">Beneficios de ser miembro</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
                {redData.membresias.beneficios.map((beneficio, index) => (
                  <div
                    key={index}
                    className="flex items-start gap-3 p-4 bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg border border-green-200 hover:border-[#0b742f] hover:shadow-md transition-all duration-200"
                  >
                    <CheckCircle className="w-5 h-5 text-[#0b742f] flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">{beneficio}</span>
                  </div>
                ))}
              </div>

              {/* Precio e Inversión con Datos Bancarios */}
              <div className="bg-gradient-to-r from-[#0b742f] to-[#085a24] text-white rounded-lg p-6 mb-8">
                <div className="flex flex-col md:flex-row items-center justify-between gap-4 mb-6">
                  <div>
                    <h3 className="text-2xl font-bold mb-2">Inversión por membresía</h3>
                    <div 
                      className="text-white/90"
                      dangerouslySetInnerHTML={{ __html: redData.membresias.inversion.descripcion }}
                    />
                  </div>
                  <div className="text-center">
                    <div className="text-5xl font-bold">${redData.membresias.inversion.monto}</div>
                    <p className="text-white/90 text-sm mt-1">{redData.membresias.inversion.moneda}</p>
                  </div>
                </div>

                {datosBancarios && (
                  <div className="border-t border-white/20 pt-6">
                    <h4 className="text-xl font-bold mb-4 flex items-center gap-2">
                      <Building2 className="w-5 h-5" />
                      Datos para transferencia bancaria
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                      <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
                        <p className="text-white/70 mb-1">Beneficiario</p>
                        <p className="font-semibold">{datosBancarios.titular}</p>
                      </div>
                      <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
                        <p className="text-white/70 mb-1">Banco</p>
                        <p className="font-semibold">{datosBancarios.banco}</p>
                      </div>
                      <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
                        <p className="text-white/70 mb-1">Cuenta</p>
                        <p className="font-semibold font-mono">{datosBancarios.numeroCuenta}</p>
                      </div>
                      <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
                        <p className="text-white/70 mb-1">CLABE Interbancaria</p>
                        <p className="font-semibold font-mono">{datosBancarios.clabe}</p>
                      </div>
                      {datosBancarios.sucursal && (
                        <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 md:col-span-2">
                          <p className="text-white/70 mb-1">Sucursal</p>
                          <p className="font-semibold">{datosBancarios.sucursal}</p>
                        </div>
                      )}
                      {datosBancarios.swift && (
                        <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
                          <p className="text-white/70 mb-1">SWIFT</p>
                          <p className="font-semibold font-mono">{datosBancarios.swift}</p>
                        </div>
                      )}
                    </div>
                    {redData?.contacto?.correo && (
                      <div className="bg-white/15 border-l-4 border-white/60 p-4 rounded mt-4">
                        <p className="text-sm text-white">
                          <strong>Importante:</strong> Envía tu comprobante de pago al correo{' '}
                          <a
                            href={`mailto:${redData.contacto.correo}`}
                            className="underline font-semibold"
                          >
                            {redData.contacto.correo}
                          </a>
                          {' '}para confirmar tu inscripción.
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Contacto */}
              <div className="bg-blue-50 rounded-lg p-6 border-l-4 border-blue-600">
                <div className="flex items-center gap-3">
                  <Mail className="w-6 h-6 text-blue-600" />
                  <div>
                    <p className="font-semibold text-gray-900">{redData.contacto.mensajeInformes}</p>
                    <a href={`mailto:${redData.contacto.correo}`} className="text-blue-600 font-bold text-lg hover:underline">
                      {redData.contacto.correo}
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Formulario de Solicitud */}
        <section className="mb-12">
          <div className="bg-white rounded-lg shadow-xl overflow-hidden border-t-4 border-[#0b742f]">
            <div className="p-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-4 flex items-center gap-3">
                <FileText className="w-8 h-8 text-[#0b742f]" />
                Formulario de solicitud de ingreso
              </h2>
              <p className="text-gray-600 mb-6">Los campos marcados con * son obligatorios</p>

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Success Message */}
                {isSuccess && (
                  <div className="bg-green-50 border-2 border-green-500 text-green-800 p-4 rounded-lg flex items-start gap-3">
                    <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-semibold">¡Formulario enviado exitosamente!</p>
                      <p className="text-sm mt-1">Nos pondremos en contacto pronto.</p>
                    </div>
                  </div>
                )}

                {/* Error Message */}
                {submitError && (
                  <div className="bg-red-50 border-2 border-red-500 text-red-800 p-4 rounded-lg">
                    <p className="font-semibold">Error al enviar el formulario</p>
                    <p className="text-sm mt-1">{submitError}</p>
                  </div>
                )}

                {/* Tipo de registro */}
                <div>
                  <label className="block text-gray-700 font-semibold mb-3">
                    Registrarse como: <span className="text-red-600">*</span>
                  </label>
                  <div className="space-y-2">
                    <label className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg border-2 border-gray-200 hover:border-[#0b742f] cursor-pointer transition-colors">
                      <input
                        type="radio"
                        name="tipo"
                        value="docente"
                        checked={formData.tipo === 'docente'}
                        onChange={handleInputChange}
                        className="w-4 h-4 text-[#0b742f]"
                      />
                      <span className="text-gray-800 font-medium">Docente Investigador</span>
                    </label>
                    <label className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg border-2 border-gray-200 hover:border-[#0b742f] cursor-pointer transition-colors">
                      <input
                        type="radio"
                        name="tipo"
                        value="grupo"
                        checked={formData.tipo === 'grupo'}
                        onChange={handleInputChange}
                        className="w-4 h-4 text-[#0b742f]"
                      />
                      <span className="text-gray-800 font-medium">Grupo de Investigación</span>
                    </label>
                  </div>
                </div>

                {/* Nombre Completo */}
                <div>
                  <label className="block text-gray-700 font-semibold mb-2">
                    Nombre Completo <span className="text-red-600">*</span>
                  </label>
                  <input
                    type="text"
                    name="nombre"
                    value={formData.nombre}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[#0b742f] focus:outline-none transition-colors"
                    placeholder="Ingrese su nombre completo"
                  />
                </div>

                {/* Email */}
                <div>
                  <label className="block text-gray-700 font-semibold mb-2">
                    Email <span className="text-red-600">*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[#0b742f] focus:outline-none transition-colors"
                    placeholder="correo@ejemplo.com"
                  />
                </div>

                {/* Línea de Investigación */}
                <div>
                  <label className="block text-gray-700 font-semibold mb-2">
                    Línea de Investigación <span className="text-red-600">*</span>
                  </label>
                  <input
                    type="text"
                    name="linea"
                    value={formData.linea}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[#0b742f] focus:outline-none transition-colors"
                    placeholder="Ingrese su línea de investigación"
                  />
                </div>

                {/* Institución */}
                <div>
                  <label className="block text-gray-700 font-semibold mb-2">
                    Institución <span className="text-red-600">*</span>
                  </label>
                  <input
                    type="text"
                    name="institucion"
                    value={formData.institucion}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[#0b742f] focus:outline-none transition-colors"
                    placeholder="Ingrese su institución"
                  />
                </div>

                {/* Renovación membresía */}
                <div className="bg-emerald-50 rounded-lg border-2 border-emerald-100 p-5">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Renovación membresía</h3>
                  <label className="flex items-start gap-3 cursor-pointer mb-4">
                    <input
                      type="checkbox"
                      name="renovacionMembresia"
                      checked={formData.renovacionMembresia}
                      onChange={handleInputChange}
                      className="w-5 h-5 text-[#0b742f] mt-0.5 flex-shrink-0"
                    />
                    <span className="text-gray-700 font-medium">
                      Marcar si esta solicitud corresponde a renovación de membresía.
                    </span>
                  </label>
                  <label className="block text-gray-700 font-semibold mb-2">
                    Número de membresía a renovar
                  </label>
                  <input
                    type="text"
                    name="numeroMembresia"
                    value={formData.numeroMembresia}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border-2 border-emerald-200 rounded-lg focus:border-[#0b742f] focus:outline-none transition-colors bg-white"
                    placeholder="Ingrese el número de membresía"
                  />
                </div>

                {/* Mensaje */}
                <div>
                  <label className="block text-gray-700 font-semibold mb-2">
                    Tu mensaje
                  </label>
                  <textarea
                    name="mensaje"
                    value={formData.mensaje}
                    onChange={handleInputChange}
                    rows={5}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[#0b742f] focus:outline-none transition-colors resize-none"
                    placeholder="Mensaje adicional (opcional)"
                  />
                </div>

                {/* Checkbox de aceptación */}
                <div>
                  <label className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg border-2 border-gray-200 cursor-pointer hover:border-[#0b742f] transition-colors">
                    <input
                      type="checkbox"
                      name="acepto"
                      checked={formData.acepto}
                      onChange={handleInputChange}
                      required
                      className="w-5 h-5 text-[#0b742f] mt-0.5 flex-shrink-0"
                    />
                    <span className="text-gray-700 text-sm">
                      <span className="text-red-600 font-semibold">* Acepto:</span> Con el envío de este formulario aceptas el tratamiento y gestión de los datos personales según nuestra Política de Privacidad.
                    </span>
                  </label>
                </div>

                {/* Botón de envío */}
                <div className="flex justify-center pt-4">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="bg-gradient-to-r from-[#0b742f] to-[#085a24] text-white px-8 py-4 rounded-lg font-bold text-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 flex items-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="w-6 h-6 animate-spin" />
                        Enviando...
                      </>
                    ) : (
                      <>
                        <Send className="w-6 h-6" />
                        Enviar Solicitud
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </section>

      </div>
    </div>
  );
};

export default RedRiel;
